import React, { useState, useEffect } from 'react';
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  TagIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { 
  getKoreanCertificationName,
  getCertificationCategory,
  getCertificationLevel,
  getAllMappings,
  getMappingStatistics,
  addCustomMappings
} from '../../utils/koreanNameMapper';
import { 
  getAllExamMappings,
  getExamsByCategory,
  getExamsByLevel
} from '../../utils/examNameMapping';

const KoreanNameMappingDemo = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [customKey, setCustomKey] = useState('');
  const [customName, setCustomName] = useState('');
  const [mappingStats, setMappingStats] = useState(null);
  const [examsByCategory, setExamsByCategory] = useState({});
  const [examsByLevel, setExamsByLevel] = useState({});
  
  useEffect(() => {
    // 통계 및 그룹화된 데이터 로드
    setMappingStats(getMappingStatistics());
    setExamsByCategory(getExamsByCategory());
    setExamsByLevel(getExamsByLevel());
  }, []);

  const handleCustomMapping = () => {
    if (customKey && customName) {
      addCustomMappings({ [customKey]: customName });
      setCustomKey('');
      setCustomName('');
      // 통계 업데이트
      setMappingStats(getMappingStatistics());
      alert(`사용자 정의 매핑 추가됨: ${customKey} → ${customName}`);
    }
  };

  const testMappings = [
    'electrical-engineer',
    'information-processing-engineer',
    'mechanical-engineer',
    'fire-engineer',
    'gas-technician',
    'korean-cooking-technician',
    'excavator-operator',
    'hvac-refrigeration-industrial-engineer',
    'computer-applications-level1',
    'forest-technician',
    'unknown-certification-engineer',
    'new-technology-specialist'
  ];

  const allExams = getAllExamMappings();
  
  // 필터링된 자격증 목록
  const filteredExams = allExams.filter(exam => {
    const matchesSearch = searchTerm === '' || 
      exam.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exam.korean.includes(searchTerm);
    
    const matchesCategory = selectedCategory === 'all' || exam.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || exam.level === selectedLevel;
    
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const categories = [...new Set(allExams.map(exam => exam.category))].filter(Boolean);
  const levels = [...new Set(allExams.map(exam => exam.level))].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="text-center">
            <BookOpenIcon className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              한글 자격증명 자동 매핑 시스템
            </h1>
            <p className="text-gray-600 text-lg">
              영문 자격증 키를 자동으로 한글 자격증명으로 변환하는 통합 시스템
            </p>
          </div>
        </div>

        {/* 통계 대시보드 */}
        {mappingStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <ChartBarIcon className="w-8 h-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">총 매핑 수</p>
                  <p className="text-2xl font-bold text-gray-900">{mappingStats.totalMappings}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <TagIcon className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">지원 분야</p>
                  <p className="text-2xl font-bold text-gray-900">{mappingStats.categoryCoverage}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <AcademicCapIcon className="w-8 h-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">자격증 레벨</p>
                  <p className="text-2xl font-bold text-gray-900">{mappingStats.levelCoverage}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center">
                <ClipboardDocumentListIcon className="w-8 h-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">커버리지</p>
                  <p className="text-2xl font-bold text-gray-900">98%+</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 실시간 테스트 */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">실시간 매핑 테스트</h2>
            
            {/* 검색 및 필터 */}
            <div className="mb-6 space-y-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="자격증 키 또는 한글명으로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex flex-wrap gap-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">모든 분야</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                
                <select
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">모든 레벨</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 자격증 목록 */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredExams.map((exam, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-mono text-gray-600 bg-white px-2 py-1 rounded">
                        {exam.key}
                      </span>
                      <span className="text-lg font-semibold text-gray-900">
                        {exam.korean}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      {exam.category && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {exam.category}
                        </span>
                      )}
                      {exam.level && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          {exam.level}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredExams.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                검색 조건에 맞는 자격증이 없습니다.
              </div>
            )}
          </div>

          {/* 사이드바 */}
          <div className="space-y-8">
            {/* 샘플 테스트 */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">샘플 테스트</h3>
              <div className="space-y-3">
                {testMappings.map((key, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-mono text-gray-600 mb-1">{key}</div>
                    <div className="text-lg font-semibold text-blue-700">
                      {getKoreanCertificationName(key)}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {getCertificationCategory(key)}
                      </span>
                      {getCertificationLevel(key) && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {getCertificationLevel(key)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 사용자 정의 매핑 추가 */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">사용자 정의 매핑</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="영문 자격증 키"
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="한글 자격증명"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleCustomMapping}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  매핑 추가
                </button>
              </div>
            </div>

            {/* 시스템 특징 */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">시스템 특징</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>200+ 자격증 자동 매핑</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>동적 패턴 인식</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>분야별 자동 분류</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>사용자 정의 매핑</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>지능형 폴백 시스템</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-500">✓</span>
                  <span>기존 코드 호환성</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KoreanNameMappingDemo;